import { BrDivider, BrLoading } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import ErrorMessage from 'components/errorMessage/errorMessage';
import ResumoCard from 'components/resumoCard/resumoCard';
import StatusPagamento from 'components/statusPagamento/statusPagamento';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { IDadosPorAliquotaResumo, IReceberDadosGuiaRecolhimentoResumo } from 'interfaces/IGuiaRecolhimento';
import { useBackEndRequests } from 'pages/TitularArrendatario/lib/backEndRequests';
import { formatarCompetencia } from 'utils/formatCompetencia';
import { formatCurrency } from 'utils/formatCurrency';
import { formatarDataISOpBR } from 'utils/formatDate';
import { formatPercentual } from 'utils/formatPercentual';
import styles from './resumoSection.module.scss';

interface IResumoSectionProps {
  step: number;
  opcaoPagamento: string;
  setIsModalOpcoesPagamentoOpened: React.Dispatch<React.SetStateAction<boolean>>;
  isPrimeiroAdquirente?: boolean;
  copiarGuiaRecolhimento: (params: any) => void;
}

const ResumoSection = ({
  step,
  opcaoPagamento,
  setIsModalOpcoesPagamentoOpened,
  isPrimeiroAdquirente = false,
  copiarGuiaRecolhimento,
}: IResumoSectionProps) => {
  const urlParams = new URLSearchParams(window.location.search);
  const guiaRecolhimentoId = urlParams.get('id') || '';

  const { getResumo } = useBackEndRequests();

  const {
    data: resumoData,
    isLoading,
    refetch,
    error,
  } = useQuery<IReceberDadosGuiaRecolhimentoResumo, IErrorWithSentryId>({
    queryKey: ['getResumoQuery', guiaRecolhimentoId],
    queryFn: () => getResumo(guiaRecolhimentoId),
    refetchOnWindowFocus: false,
  });

  const getLabelsAndPropertiesForResumoCard = (
    data: IDadosPorAliquotaResumo,
    isTotal: boolean,
    isTotalFinal: boolean
  ): { label: string; value: any; large?: boolean; showInSummary?: boolean; format?: Function }[] => {
    if (!data) return [];

    let labels = [];

    if (isTotal) {
      labels = [
        {
          label: 'CFEM apurada no mês de ocorrência do fato gerador',
          value: data.cfemMesOcorrenciaFatoGerador?.toString(),
          showInSummary: true,
          format: formatCurrency,
        },
        {
          label: 'Correção Monetária',
          value: data.correcaoMonetaria?.toString(),
          showInSummary: true,
          format: formatCurrency,
        },
        { label: 'Juros', value: data.juros?.toString(), showInSummary: true, format: formatCurrency },
        { label: 'Multa', value: data.multas?.toString(), showInSummary: true, format: formatCurrency },
        {
          label: 'CFEM a Recolher',
          value: data.cfemNoMesAtual?.toString(),
          showInSummary: true,
          format: formatCurrency,
        },
      ];

      if (isTotalFinal) {
        labels = [
          ...labels,
          {
            label: 'Período Competência',
            value: data.periodoApuracao?.toString(),
            showInSummary: true,
            format: formatarCompetencia,
          },
          {
            label: 'Data vencimento',
            value: data.dataVencimento?.toString(),
            showInSummary: true,
            format: formatarDataISOpBR,
          },
          {
            label: 'Data pagamento',
            value: data.dataPagamento?.toString(),
            showInSummary: true,
            format: formatarDataISOpBR,
          },
          { label: 'Dias em atraso', value: data.diasEmAtraso?.toString(), showInSummary: true },
        ];
      }
      return labels;
    }

    if ('aliquota' in data) {
      labels = [
        { label: 'Receita Total', value: data.receitaTotal?.toString(), showInSummary: true, format: formatCurrency },
        { label: 'Deduções', value: data.deducoes?.toString(), showInSummary: true, format: formatCurrency },
        { label: 'Base de Cálculo', value: data.baseCalculo?.toString(), showInSummary: true, format: formatCurrency },
        { label: 'Alíquota', value: data.aliquota?.toString(), showInSummary: true, format: formatPercentual },
        {
          label: 'CFEM apurada no mês de ocorrência do fato gerador',
          value: data.cfemMesOcorrenciaFatoGerador?.toString(),
          large: true,
          format: formatCurrency,
        },
        { label: 'Correção Monetária', value: data.correcaoMonetaria?.toString(), format: formatCurrency },
        { label: 'Juros', value: data.juros?.toString(), format: formatCurrency },
        { label: 'Multa', value: data.multas?.toString(), format: formatCurrency },
        { label: 'CFEM a Recolher', value: data.cfemNoMesAtual?.toString(), format: formatCurrency },
      ];
      return labels;
    }

    return [];
  };

  const conteudoPagina = () => {
    if (isLoading) {
      return (
        <div data-testid='loading-container'>
          <BrLoading large />
        </div>
      );
    }

    if (error && !resumoData) {
      return (
        <ErrorMessage
          errorMessage='Erro ao carregar dados do resumo.'
          error={error}
          errorTitle='Erro ao carregar resumo'
          onClickFunction={() => refetch()}
        />
      );
    }

    if (resumoData) {
      return (
        <>
          <div className={styles.container}>
            <>
              <ResumoCard
                key='aliquotas-normais'
                title='Operações com alíquotas normais'
                data={getLabelsAndPropertiesForResumoCard(resumoData.resumoItensAliquotaNormal, false, false)}
              />
              <ResumoCard
                key='aliquotas-reduzidas'
                title='Operações com alíquotas reduzidas'
                data={getLabelsAndPropertiesForResumoCard(resumoData.resumoItensAliquotaReduzida, false, false)}
              />
              {!isPrimeiroAdquirente && (
                <>
                  <ResumoCard
                    key='aliquotas-especificas'
                    title='Operações com alíquotas específicas'
                    data={getLabelsAndPropertiesForResumoCard(resumoData.resumoItensAliquotaEspecificas, false, false)}
                  />
                  <ResumoCard
                    key='aliquotas-especificas-reduzidas'
                    title='Operações com alíquotas específicas reduzidas'
                    data={getLabelsAndPropertiesForResumoCard(
                      resumoData.resumoItensAliquotaEspecificasReduzidas,
                      false,
                      false
                    )}
                  />
                </>
              )}
            </>
          </div>
          {step === 4 && (
            <div className={styles.totalPrevia}>
              <ResumoCard
                key={'total-previa'}
                title='Total'
                data={getLabelsAndPropertiesForResumoCard(resumoData.resumoTotal, true, false)}
                showExpandAction={false}
              />
            </div>
          )}
          {step === 5 && (
            <>
              <BrDivider className={styles.divider} />
              <div className={styles.confirmationMessage}>
                <div className={styles.totalFinal}>
                  <ResumoCard
                    key={'total-final'}
                    title='Total'
                    data={getLabelsAndPropertiesForResumoCard(resumoData.resumoTotal, true, true)}
                    showExpandAction={false}
                  />
                </div>
                <StatusPagamento
                  stepStatus={1}
                  setIsModalOpcoesPagamentoOpened={setIsModalOpcoesPagamentoOpened}
                  copiarGuiaRecolhimento={copiarGuiaRecolhimento}
                />
              </div>
            </>
          )}
        </>
      );
    }

    return <span>Nenhum dado de resumo disponível.</span>;
  };

  return conteudoPagina();
};

export default ResumoSection;
